import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSimpliCity } from 'cms/hooks/use-simplicity';
import { regular, solid } from 'icons';
import { LoginSignupModal } from 'mydnv/components/call-to-action-subscribe/login-signup-modal';
import { Config } from 'config';
import * as subscribeService from '../../../mydnv/services/subscription-service';
import { getAccessToken } from 'mydnv/utils/access-token';
import { MyDNVContext } from 'mydnv/context/mydnv-context';

const SubscribeButton = (props) => {
    const { webpage, path, model } = useSimpliCity();
    const { type } = model || {};
    const { setActionType, webpageGuid, setSubtopic } = props;

    const { account, emailVerified, isAuthenticated, instance } = useContext(MyDNVContext);
    const loginRequest = Config.MyDNV.loginRequest;

    const [subscribed, setSubscribed] = useState(null);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [subscriptionTopic, setSubscriptionTopic] = useState('');

    const request = useMemo(() => {
        return {
            ...loginRequest,
            account: account,
        };
    }, [account, loginRequest]);

    let wpGuid = useMemo(() => {
        if (webpageGuid) {
            // advisories/stories/news pages pass in the guid directly from config instead
            return webpageGuid;
        } else {
            return webpage.guid;
        }
    }, [webpage, webpageGuid]);

    const fetchSubscriptions = useCallback(
        async (args) => {
            let response = await subscribeService.getSubscriptionTopics(args);
            // setSubscriptions(response.data);
            const topic = response.data.filter((s) => s.guid === webpageGuid);
            if (topic.length > 0) {
                setSubscriptionTopic(topic[0].name);
                setSubtopic(topic[0].name);
            }
            // console.log(response.data);
        },
        [setSubtopic, webpageGuid]
    );

    const getUserSubscribe = useCallback(
        async (args) => {
            let response = await subscribeService.getUserSubscribe(args);
            setSubscribed(response.data);
        },
        [setSubscribed]
    );

    const subscribeClicked = () => {
        if (isAuthenticated) {
            getAccessToken(
                request,
                instance,
                handleSubscribe,
                { guid: wpGuid },
                {
                    key: 'signin-redirect-action',
                    value: 'subscribe',
                }
            );
        } else {
            setIsLoginModalOpen(true);
        }
    };

    const handleModal = (isOpen) => {
        setIsLoginModalOpen(isOpen);
    };

    const handleSubscribe = useCallback(
        async (args) => {
            await subscribeService
                .postTopicSubscribe(args)
                .then(() => {
                    setSubscribed(true);
                    setActionType('subscribed');
                    // console.log(res);
                })
                .catch(() => {
                    // console.log(err);
                    setActionType('error');
                });
        },
        [setActionType, setSubscribed]
    );

    const handleUnsubscribe = useCallback(
        async (args) => {
            await subscribeService
                .postTopicUnsubscribe(args)
                .then(() => {
                    setSubscribed(false);
                    setActionType('unsubscribed');
                    // console.log(res);
                })
                .catch(() => {
                    // console.log(err);
                    setActionType('error');
                });
        },
        [setActionType]
    );

    const showButton = () => {
        const showBtnlist = Config.MyDNV.showSubscribeButtonList?.toString().split(',') || [];
        const showBtnModelTypes = Config.MyDNV.showSubscribeButtonListModelTypes?.toString().split(',') || [];
        return (
            (showBtnlist.includes(webpageGuid) || showBtnlist.includes(path) || showBtnModelTypes.includes(type)) &&
            subscriptionTopic !== ''
        );
        // 'engagement opportunities' & 'significant applications list' should show 'subscribe' button and no follow button, everything else should show follow button
        // return showBtnlist.includes(path) || showBtnModelTypes.includes(type);
    };

    useEffect(() => {
        fetchSubscriptions(); // TODO move to context
        // on page load, if user is being redirected from B2C login page
        if (isAuthenticated && emailVerified && !!account) {
            if (localStorage.getItem('signin-redirect-action') == 'subscribe') {
                getAccessToken(
                    request,
                    instance,
                    handleSubscribe,
                    { guid: wpGuid },
                    {
                        key: 'signin-redirect-action',
                        value: 'subscribe',
                    }
                );
            } else if (localStorage.getItem('signin-redirect-action') == 'unsubscribe') {
                getAccessToken(
                    request,
                    instance,
                    handleUnsubscribe,
                    { guid: wpGuid },
                    {
                        key: 'signin-redirect-action',
                        value: 'unsubscribe',
                    }
                );
            } else {
                getAccessToken(request, instance, getUserSubscribe, { guid: wpGuid }, null);
            }
        }
    }, [
        account,
        fetchSubscriptions,
        getUserSubscribe,
        handleSubscribe,
        handleUnsubscribe,
        instance,
        isAuthenticated,
        emailVerified,
        request,
        webpage,
        wpGuid,
    ]);

    const getBtnClassNames = () => {
        let btnClass = 'subscribe ';
        if (subscribed) {
            btnClass += 'active ';
        }

        return btnClass;
    };

    const getSubscriptionTopicFormattedName = () => {
        switch (subscriptionTopic.toLowerCase()) {
            case 'council documents':
                return 'Council documents';

            default:
                return subscriptionTopic.toLowerCase();
        }
    };

    const getFAIcon = () => {
        if (subscribed) {
            switch (subscriptionTopic.toLowerCase()) {
                case 'new applications':
                    return solid.faBuilding;
                case 'news':
                    return solid.faNewspaper;
                case 'advisories':
                    return solid.faSquareExclamation;
                case 'council documents':
                    return solid.faBuildingColumns;
                case 'input opportunities':
                    return solid.faBullhorn;
                case 'stories':
                    return solid.faBookBlank;
            }
        } else {
            switch (subscriptionTopic.toLowerCase()) {
                case 'new applications':
                    return regular.faBuilding;
                case 'news':
                    return regular.faNewspaper;
                case 'advisories':
                    return regular.faSquareExclamation;
                case 'council documents':
                    return regular.faBuildingColumns;
                case 'input opportunities':
                    return regular.faBullhorn;
                case 'stories':
                    return regular.faBookBlank;
            }
        }
    };

    useEffect(() => {
        if (!!subscribed && !isAuthenticated) {
            setSubscribed(false);
        }
    }, [isAuthenticated, subscribed, setSubscribed]);

    return (
        showButton() && (
            <>
                {(!isAuthenticated || (!subscribed && subscribed !== null)) && (
                    <button className={getBtnClassNames()} onClick={subscribeClicked}>
                        <span>Subscribe to {getSubscriptionTopicFormattedName()}</span>{' '}
                        <FontAwesomeIcon icon={getFAIcon()} />
                    </button>
                )}

                {!!subscribed && (
                    <button
                        className={getBtnClassNames()}
                        onClick={() => {
                            getAccessToken(
                                request,
                                instance,
                                handleUnsubscribe,
                                { guid: webpageGuid },
                                {
                                    key: 'signin-redirect-action',
                                    value: 'unsubscribe',
                                },
                                () => {
                                    setIsLoginModalOpen(true);
                                }
                            );
                        }}
                    >
                        <span>Subscribed to {getSubscriptionTopicFormattedName()} </span>
                        <FontAwesomeIcon icon={getFAIcon()} />
                    </button>
                )}

                <LoginSignupModal isOpen={isLoginModalOpen} setModalOpen={handleModal} action={'subscribe'} />
            </>
        )
    );
};

export { SubscribeButton };
